import type { PlayerSearchFilters } from "@teamz/common";
import type { UrlKeys } from "nuqs";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ATPRankingBrackets, ITFRankingBrackets, matchAndClubCountryCodes, playerFFTranking, playerGenders, UTRRankingBrackets } from "@teamz/common";
import { Lock } from "lucide-react";
import { parseAsStringLiteral, useQueryStates } from "nuqs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { ListSkeleton } from "@/components/skeleton/list-skeleton";
import { buttonVariants } from "@/components/ui/button.styles";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { routes } from "@/routes";

import { PlayerTeaserFilters } from "./player-teaser-filters";
import { PlayerTeaserItem } from "./player-teaser-item";

// Les critères vivent dans l'URL, pas dans un état local : le retour depuis une
// fiche joueur se fait par navigate(-1), donc c'est l'URL précédente qui décide
// si la recherche est retrouvée ou perdue.
const searchParams = {
  country: parseAsStringLiteral(matchAndClubCountryCodes),
  gender: parseAsStringLiteral(playerGenders),
  FFTRanking: parseAsStringLiteral(playerFFTranking),
  UTRBracket: parseAsStringLiteral(UTRRankingBrackets),
  ATPBracket: parseAsStringLiteral(ATPRankingBrackets),
  ITFBracket: parseAsStringLiteral(ITFRankingBrackets),
};
const searchParamsKeys: UrlKeys<typeof searchParams> = {
  country: "c",
  gender: "g",
  FFTRanking: "fft",
  UTRBracket: "utr",
  ATPBracket: "atp",
  ITFBracket: "itf",
};

// Liste joueurs en mode freemium : l'API ne renvoie que des profils anonymisés
// (pays, âge, classements, sexe, jamais de nom ni de photo), à l'exception des
// profils vitrines servis en clair en tête de liste. Une seule liste, un seul
// décompte : celui de la barre de filtres, qui porte sur toute la base pendant
// que la liste n'en montre qu'un extrait. L'écart entre les deux est justement
// l'argument d'abonnement, rappelé sous la liste.
export function PlayerListTeaser() {
  const { t } = useTranslation(["common", "profile"]);
  const trpc = useTRPC();
  const [filters, setSearchFilters] = useQueryStates(searchParams, { urlKeys: searchParamsKeys });

  // nuqs n'efface que les clés qu'on lui passe : on les réécrit toutes, sinon
  // « Réinitialiser » laisserait les critères absents de l'objet dans l'URL.
  const setFilters = (next: PlayerSearchFilters) => setSearchFilters({
    country: next.country ?? null,
    gender: next.gender ?? null,
    FFTRanking: next.FFTRanking ?? null,
    UTRBracket: next.UTRBracket ?? null,
    ATPBracket: next.ATPBracket ?? null,
    ITFBracket: next.ITFBracket ?? null,
  });

  // placeholderData : la liste et le décompte précédents restent affichés
  // pendant le rechargement, sinon la page se vide à chaque critère changé.
  const { data: players, status, isFetching } = useQuery(trpc.player.listPlayersTeaser.queryOptions(filters, { placeholderData: keepPreviousData }));
  const { data: playersCount } = useQuery(trpc.player.countPlayersTeaser.queryOptions(filters, { placeholderData: keepPreviousData }));

  const matchingCount = playersCount?.count ?? 0;
  const hasActiveFilters = Object.values(filters).some(value => value !== null && value !== undefined);

  return (
    <div className="container flex flex-col gap-3 pb-4 md:p-4">
      {/* Sur mobile, l'argumentaire et la barre de filtres forment un seul
          bandeau pleine largeur, sans bordure ni interstice : la liste commence
          plus haut dans l'écran. À partir de md ils redeviennent deux blocs
          arrondis séparés. */}
      <div className="flex flex-col md:gap-3">
        <div className="flex flex-col items-center gap-2 border-orange-500 bg-orange-100/75 px-3 py-2.5 text-center md:rounded-xl md:border md:px-4 md:py-3 lg:flex-row lg:justify-between lg:gap-3 lg:text-start">
          <div className="flex flex-col items-center gap-1.5 lg:flex-row lg:gap-3">
            <div className="flex shrink-0 items-center justify-center rounded-lg bg-orange-500 p-1 md:p-1.5">
              <Lock className="size-3.5 text-orange-100 md:size-4" />
            </div>
            <p className="text-balance text-xs font-medium md:text-sm">
              {t("common:freemium.players-banner-description")}
            </p>
          </div>
          <Link to={routes.plans} className={cn(buttonVariants({ variant: "default" }), "h-10 w-full shrink-0 text-xs sm:w-auto md:text-sm lg:h-11")}>
            {t("common:freemium.access-all-players")}
          </Link>
        </div>

        <PlayerTeaserFilters filters={filters} onFiltersChange={setFilters} />
      </div>

      {status === "pending" && <ListSkeleton />}

      {status === "success" && (
        <div className={cn("flex flex-col gap-2 transition-opacity", isFetching && "opacity-60")}>
          {/* Seul décompte de la page, juste au-dessus de la liste : il porte sur
              toute la base et suit les critères. */}
          <p aria-live="polite" className="px-2 text-sm font-medium text-neutral-700 md:px-0">
            {matchingCount === 0 && t("common:freemium.search-empty")}
            {matchingCount > 0 && (hasActiveFilters
              ? t("common:freemium.search-result", { count: matchingCount })
              : t("common:freemium.players-count", { count: matchingCount }))}
          </p>

          <div className="flex flex-col gap-4 px-2 md:gap-0 md:rounded-[18px] md:border md:border-neutral-400 md:p-0">
            {players.length
              ? players.map(player => (
                  <PlayerTeaserItem key={player.id} player={player} />
                ))
              : (
                  <span className="rounded-[18px] border border-neutral-400 bg-white p-4 text-center text-neutral-600 md:border-0">
                    {t("common:no-player-found")}
                  </span>
                )}
          </div>
        </div>
      )}
    </div>
  );
}
